import React, { useEffect, useState } from "react"
import { useDispatch } from "react-redux"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import {
	faArrowLeft,
	faSync,
	faPlus,
	faTrash,
} from "@fortawesome/free-solid-svg-icons"
import {
	saveEntiteV2,
	deleteEntiteV2,
	deleteClientAdresse,
	deleteEntitePersonne,
	deleteEntiteFourniture,
	deleteEntiteAssurance,
} from "../../../store/actions/entiteActions"
import { fetchAutreEntites } from "../../../store/actions/autreActions"
import {
	getBaseAdresse,
	getBasePersonne,
	getBaseFourniture,
} from "../../../utilities/defaultObjects"
import {
	ENTITE_TYPE_SOCIETE,
	ENTITE_TYPE_ASSURANCE,
	ENTITE_TYPE_GARAGE,
	ENTITE_TYPE_POLICE,
	ENTITE_TYPE_BUREAU_EXPERT,
	ENTITE_TYPE_SOUS_TRAITANT,
	ENTITE_TYPE_PERSONNE_PHYSIQUE,
	ENTITE_TYPE_ASSISTANCE,
	ENTITE_TYPES,
} from "../entite/Entite"
import { PERSONNE_APPELLATION } from "../entite/Personne"
import { COLORS } from "../../../utilities/Constants"
import { generalStyles as gs } from "../../styles/styles"
import CustomModal from "../../basics/interface/CustomModal"
import EntiteAdressesV2 from "../entite/EntiteAdressesV2"
import MainOeuvreRabaisV2 from "../entite/MainOeuvreRabaisV2"

const typeLabel = (t) => {
	const f = ENTITE_TYPES.find((e) => e[0] === t)
	return f ? f[1] : "Entité"
}

const styles = {
	header: {
		display: "flex",
		alignItems: "center",
		gap: 16,
		marginBottom: 18,
	},
	back: {
		display: "inline-flex",
		alignItems: "center",
		gap: 8,
		fontSize: 13,
		fontWeight: 500,
		color: COLORS.textMuted,
		background: "none",
		border: "none",
		cursor: "pointer",
		padding: 0,
		fontFamily: "inherit",
		flexShrink: 0,
	},
	titleWrap: { flex: 1, minWidth: 0 },
	kicker: {
		fontSize: 12,
		fontWeight: 600,
		letterSpacing: "0.06em",
		textTransform: "uppercase",
		color: COLORS.textLight,
	},
	name: {
		fontSize: 20,
		fontWeight: 600,
		color: COLORS.text,
		marginTop: 2,
		whiteSpace: "nowrap",
		overflow: "hidden",
		textOverflow: "ellipsis",
	},
	actions: { display: "flex", gap: 10, flexShrink: 0 },
	btnDanger: (busy) => ({
		padding: "10px 18px",
		borderRadius: 10,
		border: `1px solid ${COLORS.danger}`,
		backgroundColor: COLORS.dangerLight,
		color: COLORS.danger,
		fontSize: 13,
		fontWeight: 500,
		fontFamily: "inherit",
		cursor: busy ? "not-allowed" : "pointer",
		opacity: busy ? 0.5 : 1,
	}),
	itemHead: {
		display: "flex",
		alignItems: "center",
		justifyContent: "space-between",
		minHeight: 18,
		marginBottom: 10,
	},
	itemLabel: {
		fontSize: 11,
		fontWeight: 600,
		color: COLORS.textLight,
		letterSpacing: "0.08em",
		textTransform: "uppercase",
	},
	delBtn: {
		display: "inline-flex",
		alignItems: "center",
		justifyContent: "center",
		width: 28,
		height: 28,
		borderRadius: 8,
		border: `1px solid ${COLORS.border}`,
		backgroundColor: COLORS.surface,
		color: COLORS.danger,
		cursor: "pointer",
		fontSize: 12,
	},
	block: {
		borderTop: `1px solid ${COLORS.border}`,
		paddingTop: 18,
		marginTop: 18,
	},
	empty: { color: COLORS.textLight, fontSize: 13, marginBottom: 14 },
}

const req = <span style={{ color: COLORS.danger }}> *</span>

// ===== Sous-cartes Personne / Fourniture =====
const PersonneCard = ({ personne, onChange, onRemove, isPolice }) => (
	<>
		<div style={styles.itemHead}>
			<span style={styles.itemLabel}>
				{`${personne.nom || ""} ${personne.prenom || ""}`.trim() ||
					"Nouvelle personne"}
			</span>
			<button style={styles.delBtn} onClick={onRemove} title="Retirer">
				<FontAwesomeIcon icon={faTrash} />
			</button>
		</div>
		<div style={gs.fieldGroup}>
			<div style={gs.fieldRow}>
				<label style={gs.label}>Appellation</label>
				<select
					style={gs.input}
					value={personne.appellation ?? 0}
					onChange={(e) =>
						onChange("appellation", parseInt(e.target.value))
					}
				>
					{PERSONNE_APPELLATION.map((a) => (
						<option key={a[0]} value={a[0]}>
							{a[1]}
						</option>
					))}
				</select>
			</div>
			<div style={gs.fieldRow}>
				<label style={gs.label}>Nom</label>
				<input
					style={gs.input}
					value={personne.nom || ""}
					onChange={(e) => onChange("nom", e.target.value)}
				/>
			</div>
			<div style={gs.fieldRow}>
				<label style={gs.label}>Prénom</label>
				<input
					style={gs.input}
					value={personne.prenom || ""}
					onChange={(e) => onChange("prenom", e.target.value)}
				/>
			</div>
			<div style={gs.fieldRow}>
				<label style={gs.label}>Téléphone</label>
				<input
					style={gs.input}
					value={personne.telephone || ""}
					onChange={(e) => onChange("telephone", e.target.value)}
				/>
			</div>
			<div style={gs.fieldRow}>
				<label style={gs.label}>E-mail</label>
				<input
					style={gs.input}
					value={personne.email || ""}
					onChange={(e) => onChange("email", e.target.value)}
				/>
			</div>
			{isPolice && (
				<div style={gs.fieldRow}>
					<label style={gs.label}>Matricule</label>
					{/* GE-13 : colonne propre Personne.matricule (unique) —
					    plus extra_data, où l'autosuggest mobile ne lit pas. */}
					<input
						style={gs.input}
						value={personne.matricule || ""}
						onChange={(e) => onChange("matricule", e.target.value)}
					/>
				</div>
			)}
		</div>
	</>
)

const FournitureCard = ({ fourniture, onChange, onRemove }) => (
	<>
		<div style={styles.itemHead}>
			<span style={styles.itemLabel}>
				{fourniture.label || "Nouvelle fourniture"}
			</span>
			<button style={styles.delBtn} onClick={onRemove} title="Retirer">
				<FontAwesomeIcon icon={faTrash} />
			</button>
		</div>
		<div style={gs.fieldGroup}>
			<div style={gs.fieldRow}>
				<label style={gs.label}>Libellé</label>
				<input
					style={gs.input}
					value={fourniture.label || ""}
					onChange={(e) => onChange("label", e.target.value)}
				/>
			</div>
			<div style={gs.fieldRow}>
				<label style={gs.label}>Opération</label>
				<select
					style={gs.input}
					value={fourniture.operation ?? 0}
					onChange={(e) =>
						onChange("operation", parseInt(e.target.value))
					}
				>
					<option value={0}>Soustraction</option>
					<option value={1}>Rabais %</option>
				</select>
			</div>
			<div style={gs.fieldRow}>
				<label style={gs.label}>Valeur</label>
				<input
					type="number"
					style={gs.input}
					value={fourniture.valeur ?? 0}
					onChange={(e) =>
						onChange(
							"valeur",
							e.target.value === ""
								? 0
								: parseFloat(e.target.value),
						)
					}
				/>
			</div>
		</div>
	</>
)

// Fiche entité (ébauche) — édition/création d'une entité non-client/non-master.
const EntiteFicheV2 = ({ entite: initial, onClose }) => {
	const dispatch = useDispatch()
	const isCreate = !initial.id

	const [data, setData] = useState(() => ({
		...initial,
		vehicules: [],
		personnes: initial.id ? initial.personnes || [] : [],
		fournitures: initial.id ? initial.fournitures || [] : [],
		assurances: initial.id ? initial.assurances || [] : [],
		adresses: initial.adresses || [getBaseAdresse()],
		extra_data: initial.extra_data || {},
	}))
	const [dirty, setDirty] = useState(false)
	const [saving, setSaving] = useState(false)
	const [deleting, setDeleting] = useState(false)
	const [showSaveModal, setShowSaveModal] = useState(false)
	const [showDeleteModal, setShowDeleteModal] = useState(false)
	const [showQuit, setShowQuit] = useState(false)
	// Confirmation de retrait d'un élément déjà sauvé : { kind, index } | null.
	const [confirmRemove, setConfirmRemove] = useState(null)
	const [removing, setRemoving] = useState(false) // suppression adresse (async)

	const showPersonnes = [
		ENTITE_TYPE_POLICE,
		ENTITE_TYPE_BUREAU_EXPERT,
	].includes(data.type)
	const isPolice = data.type === ENTITE_TYPE_POLICE
	const showFournitures = data.type === ENTITE_TYPE_SOUS_TRAITANT
	const showAssurances = data.type === ENTITE_TYPE_ASSISTANCE
	// Rabais vente MO : pertinent pour les types dont la fiche atelier lit le
	// rabais (société / assurance / privé).
	const showMoRabais = [
		ENTITE_TYPE_SOCIETE,
		ENTITE_TYPE_ASSURANCE,
		ENTITE_TYPE_PERSONNE_PHYSIQUE,
	].includes(data.type)

	// Avertissement natif (fermeture d'onglet) si modifs non sauvées.
	useEffect(() => {
		const handler = (e) => {
			if (!dirty) return
			e.preventDefault()
			e.returnValue = ""
		}
		window.addEventListener("beforeunload", handler)
		return () => window.removeEventListener("beforeunload", handler)
	}, [dirty])

	const update = (key, value) => {
		setDirty(true)
		setData((d) => ({ ...d, [key]: value }))
	}
	const updateExtra = (key, value) => {
		setDirty(true)
		setData((d) => ({
			...d,
			extra_data: { ...(d.extra_data || {}), [key]: value },
		}))
	}
	// --- Adresses ---
	const updateAdresse = (index, key, value, whole = false) => {
		setDirty(true)
		setData((d) => ({
			...d,
			adresses: d.adresses.map((a, i) =>
				i !== index ? a : whole ? value : { ...a, [key]: value },
			),
		}))
	}
	const addAdresse = () => {
		setDirty(true)
		setData((d) => ({
			...d,
			adresses: [...(d.adresses || []), getBaseAdresse()],
		}))
	}
	const removeAdresse = (index) => {
		const adr = data.adresses[index]
		// Pas sauvée → retrait local direct ; sauvée → confirmation (suppression
		// immédiate en DB).
		if (!adr || !adr.id) {
			setDirty(true)
			setData((d) => ({
				...d,
				adresses: d.adresses.filter((_, i) => i !== index),
			}))
			return
		}
		setConfirmRemove({ kind: "adresse", index })
	}
	// --- Personnes ---
	const updatePersonne = (index, key, value) => {
		setDirty(true)
		setData((d) => ({
			...d,
			personnes: d.personnes.map((p, i) =>
				i !== index ? p : { ...p, [key]: value },
			),
		}))
	}
	const addPersonne = () => {
		setDirty(true)
		setData((d) => ({
			...d,
			personnes: [...(d.personnes || []), getBasePersonne()],
		}))
	}
	const reallyRemovePersonne = (index) => {
		setDirty(true)
		setData((d) => ({
			...d,
			personnes: d.personnes.filter((_, i) => i !== index),
		}))
	}
	const removePersonne = (index) => {
		const p = data.personnes[index]
		if (p && p.id) setConfirmRemove({ kind: "personne", index })
		else reallyRemovePersonne(index)
	}
	// --- Fournitures ---
	const updateFourniture = (index, key, value) => {
		setDirty(true)
		setData((d) => ({
			...d,
			fournitures: d.fournitures.map((f, i) =>
				i !== index ? f : { ...f, [key]: value },
			),
		}))
	}
	const addFourniture = () => {
		setDirty(true)
		setData((d) => ({
			...d,
			fournitures: [...(d.fournitures || []), getBaseFourniture()],
		}))
	}
	const reallyRemoveFourniture = (index) => {
		setDirty(true)
		setData((d) => ({
			...d,
			fournitures: d.fournitures.filter((_, i) => i !== index),
		}))
	}
	const removeFourniture = (index) => {
		const f = data.fournitures[index]
		if (f && f.id) setConfirmRemove({ kind: "fourniture", index })
		else reallyRemoveFourniture(index)
	}
	// --- Assurances (entité Assistance) ---
	const updateAssurance = (index, value) => {
		setDirty(true)
		setData((d) => ({
			...d,
			assurances: d.assurances.map((a, i) =>
				i !== index ? a : { ...a, label: value },
			),
		}))
	}
	const addAssurance = () => {
		setDirty(true)
		setData((d) => ({
			...d,
			assurances: [
				...(d.assurances || []),
				{ id: Math.random(), label: "" },
			],
		}))
	}
	const reallyRemoveAssurance = (index) => {
		setDirty(true)
		setData((d) => ({
			...d,
			assurances: d.assurances.filter((_, i) => i !== index),
		}))
	}
	const removeAssurance = (index) => {
		const a = data.assurances[index]
		// id entier = déjà en base → confirmation + suppression DB ; sinon retrait local.
		if (a && a.id && Number.isInteger(a.id))
			setConfirmRemove({ kind: "assurance", index })
		else reallyRemoveAssurance(index)
	}
	// Suppression immédiate en DB (endpoint) — un message clair remonte si la FK
	// bloque (référencée ailleurs) ; l'élément reste alors dans la liste.
	const doConfirmedRemove = () => {
		if (!confirmRemove) return
		const { kind, index } = confirmRemove
		let action
		let applyKey
		if (kind === "adresse") {
			action = deleteClientAdresse(data.id, data.adresses[index].id)
			applyKey = "adresses"
		} else if (kind === "personne") {
			action = deleteEntitePersonne(data.id, data.personnes[index].id)
			applyKey = "personnes"
		} else if (kind === "assurance") {
			action = deleteEntiteAssurance(data.id, data.assurances[index].id)
			applyKey = "assurances"
		} else {
			action = deleteEntiteFourniture(data.id, data.fournitures[index].id)
			applyKey = "fournitures"
		}
		setRemoving(true)
		dispatch(action).then((updated) => {
			setRemoving(false)
			if (updated)
				setData((d) => ({ ...d, [applyKey]: updated[applyKey] }))
			setConfirmRemove(null)
		})
	}

	const save = () => {
		setSaving(true)
		dispatch(saveEntiteV2(data)).then((saved) => {
			setSaving(false)
			if (saved) {
				setDirty(false)
				dispatch(fetchAutreEntites())
				onClose()
			}
		})
	}
	const confirmDeleteEntite = () => {
		setDeleting(true)
		dispatch(deleteEntiteV2(data.id)).then((ok) => {
			setDeleting(false)
			if (ok) {
				dispatch(fetchAutreEntites())
				onClose()
			}
		})
	}

	const invalid = !(data.label || "").trim() || !(data.telephone || "").trim()
	const busy = saving || deleting

	const onBack = () => {
		if (dirty) setShowQuit(true)
		else onClose()
	}

	return (
		<div>
			<div style={styles.header}>
				<button style={styles.back} onClick={onBack}>
					<FontAwesomeIcon icon={faArrowLeft} />
					Liste
				</button>
				<div style={styles.titleWrap}>
					<div style={styles.kicker}>{typeLabel(data.type)}</div>
					<div style={styles.name}>
						{data.label || (isCreate ? "Nouvelle entité" : "—")}
					</div>
				</div>
				<div style={styles.actions}>
					{!isCreate && (
						<button
							style={styles.btnDanger(busy)}
							onClick={() => setShowDeleteModal(true)}
							disabled={busy}
						>
							Supprimer
						</button>
					)}
					<button
						style={{
							...gs.btnPrimary,
							...((busy || invalid) && {
								opacity: 0.5,
								cursor: "not-allowed",
							}),
						}}
						onClick={() => setShowSaveModal(true)}
						disabled={busy || invalid}
					>
						Enregistrer
					</button>
				</div>
			</div>

			{/* Identité */}
			<div style={gs.card}>
				<div style={gs.cardTitle}>Identité</div>
				<div style={gs.fieldGroup}>
					<div style={gs.fieldRow}>
						<label style={gs.label}>Label{req}</label>
						<input
							style={gs.input}
							value={data.label || ""}
							onChange={(e) => update("label", e.target.value)}
						/>
					</div>
					<div style={gs.fieldRow}>
						<label style={gs.label}>Téléphone{req}</label>
						<input
							style={gs.input}
							value={data.telephone || ""}
							onChange={(e) =>
								update("telephone", e.target.value)
							}
						/>
					</div>
					<div style={gs.fieldRow}>
						<label style={gs.label}>Tél. supplémentaire</label>
						<input
							style={gs.input}
							value={(data.extra_data || {}).telephone2 || ""}
							onChange={(e) =>
								updateExtra("telephone2", e.target.value)
							}
						/>
					</div>
					<div style={gs.fieldRow}>
						<label style={gs.label}>E-mail</label>
						<input
							style={gs.input}
							value={data.email || ""}
							onChange={(e) => update("email", e.target.value)}
						/>
					</div>
					{data.type === ENTITE_TYPE_SOUS_TRAITANT && (
						<div style={gs.fieldRow}>
							<label style={gs.label}>
								Prix achat main d'œuvre
							</label>
							<input
								type="number"
								style={gs.input}
								value={
									(data.extra_data || {})
										.prix_achat_unitaire_mo ?? ""
								}
								onChange={(e) =>
									updateExtra(
										"prix_achat_unitaire_mo",
										e.target.value,
									)
								}
							/>
						</div>
					)}
					{showMoRabais && (
						<MainOeuvreRabaisV2
							type={data.type}
							extraData={data.extra_data}
							updateExtra={updateExtra}
						/>
					)}
					<div style={{ ...gs.fieldRow, alignItems: "flex-start" }}>
						<label style={gs.label}>Remarques</label>
						<textarea
							style={{
								...gs.input,
								minHeight: 64,
								resize: "vertical",
							}}
							value={(data.extra_data || {}).remarques || ""}
							onChange={(e) =>
								updateExtra("remarques", e.target.value)
							}
						/>
					</div>
				</div>
			</div>

			{/* Adresses */}
			<div style={gs.mt18}>
				<EntiteAdressesV2
					adresses={data.adresses}
					updateAdresse={updateAdresse}
					addAdresse={addAdresse}
					deleteAdresse={removeAdresse}
					showExtraData={data.type === ENTITE_TYPE_GARAGE}
				/>
			</div>

			{/* Personnes (police / experts) */}
			{showPersonnes && (
				<div style={gs.mt18}>
					<div style={gs.card}>
						<div style={gs.cardTitle}>
							{isPolice ? "Policiers" : "Experts"}
						</div>
						{(data.personnes || []).length === 0 && (
							<div style={styles.empty}>Aucune personne.</div>
						)}
						{(data.personnes || []).map((p, index) => (
							<div
								key={p.id || `new-${index}`}
								style={index === 0 ? {} : styles.block}
							>
								<PersonneCard
									personne={p}
									isPolice={isPolice}
									onChange={(k, v) =>
										updatePersonne(index, k, v)
									}
									onRemove={() => removePersonne(index)}
								/>
							</div>
						))}
						<button
							type="button"
							style={{ ...gs.addBtn, marginTop: 18 }}
							onClick={addPersonne}
						>
							<FontAwesomeIcon icon={faPlus} />
							Ajouter une personne
						</button>
					</div>
				</div>
			)}

			{/* Fournitures (sous-traitant) */}
			{showFournitures && (
				<div style={gs.mt18}>
					<div style={gs.card}>
						<div style={gs.cardTitle}>Fournitures</div>
						{(data.fournitures || []).length === 0 && (
							<div style={styles.empty}>Aucune fourniture.</div>
						)}
						{(data.fournitures || []).map((f, index) => (
							<div
								key={f.id || `new-${index}`}
								style={index === 0 ? {} : styles.block}
							>
								<FournitureCard
									fourniture={f}
									onChange={(k, v) =>
										updateFourniture(index, k, v)
									}
									onRemove={() => removeFourniture(index)}
								/>
							</div>
						))}
						<button
							type="button"
							style={{ ...gs.addBtn, marginTop: 18 }}
							onClick={addFourniture}
						>
							<FontAwesomeIcon icon={faPlus} />
							Ajouter une fourniture
						</button>
					</div>
				</div>
			)}

			{showAssurances && (
				<div style={gs.mt18}>
					<div style={gs.card}>
						<div style={gs.cardTitle}>Assurances</div>
						{(data.assurances || []).length === 0 && (
							<div style={styles.empty}>
								Aucune assurance. Ajoutez les assurances
								couvertes par cette assistance.
							</div>
						)}
						{(data.assurances || []).map((a, index) => (
							<div
								key={a.id || `new-${index}`}
								style={{
									...gs.fieldRow,
									marginBottom: 8,
								}}
							>
								<input
									style={gs.input}
									value={a.label || ""}
									placeholder="Nom de l'assurance"
									onChange={(e) =>
										updateAssurance(index, e.target.value)
									}
								/>
								<button
									style={styles.delBtn}
									onClick={() => removeAssurance(index)}
									title="Retirer"
								>
									<FontAwesomeIcon icon={faTrash} />
								</button>
							</div>
						))}
						<button
							type="button"
							style={{ ...gs.addBtn, marginTop: 18 }}
							onClick={addAssurance}
						>
							<FontAwesomeIcon icon={faPlus} />
							Ajouter une assurance
						</button>
					</div>
				</div>
			)}

			{/* ===== Modals ===== */}
			{showSaveModal && (
				<CustomModal
					title="Enregistrer les modifications ?"
					onClose={busy ? () => {} : () => setShowSaveModal(false)}
					showCloseButton={!busy}
					buttons={[
						{
							title: "Non",
							styleName: "secondary",
							onClick: () => setShowSaveModal(false),
							disabled: busy,
						},
						{
							title: saving ? (
								<FontAwesomeIcon icon={faSync} spin />
							) : (
								"Oui"
							),
							styleName: "primary",
							onClick: save,
							disabled: busy,
						},
					]}
				>
					<p
						style={{
							margin: 0,
							fontSize: 13,
							color: COLORS.textMuted,
						}}
					>
						Voulez-vous enregistrer cette entité ?
					</p>
				</CustomModal>
			)}

			{showDeleteModal && (
				<CustomModal
					title="Supprimer l'entité ?"
					onClose={busy ? () => {} : () => setShowDeleteModal(false)}
					showCloseButton={!busy}
					buttons={[
						{
							title: "Non",
							styleName: "secondary",
							onClick: () => setShowDeleteModal(false),
							disabled: busy,
						},
						{
							title: deleting ? (
								<FontAwesomeIcon icon={faSync} spin />
							) : (
								"Supprimer"
							),
							styleName: "danger",
							onClick: confirmDeleteEntite,
							disabled: busy,
						},
					]}
				>
					<p
						style={{
							margin: 0,
							fontSize: 13,
							color: COLORS.textMuted,
						}}
					>
						{`« ${data.label || ""} » sera définitivement supprimée.`}
					</p>
				</CustomModal>
			)}

			{showQuit && (
				<CustomModal
					title="Quitter sans sauvegarder ?"
					subtitle="Les modifications non enregistrées seront perdues."
					onClose={() => setShowQuit(false)}
					showCloseButton={false}
					buttons={[
						{
							title: "Annuler",
							styleName: "secondary",
							onClick: () => setShowQuit(false),
						},
						{
							title: "Quitter",
							styleName: "danger",
							onClick: onClose,
						},
					]}
				/>
			)}

			{confirmRemove && (
				<CustomModal
					title={
						confirmRemove.kind === "adresse"
							? "Supprimer cette adresse ?"
							: confirmRemove.kind === "personne"
								? "Supprimer cette personne ?"
								: confirmRemove.kind === "assurance"
									? "Supprimer cette assurance ?"
									: "Supprimer cette fourniture ?"
					}
					onClose={removing ? () => {} : () => setConfirmRemove(null)}
					showCloseButton={!removing}
					buttons={[
						{
							title: "Annuler",
							styleName: "secondary",
							onClick: () => setConfirmRemove(null),
							disabled: removing,
						},
						{
							title: removing ? (
								<FontAwesomeIcon icon={faSync} spin />
							) : (
								"Supprimer"
							),
							styleName: "danger",
							onClick: doConfirmedRemove,
							disabled: removing,
						},
					]}
				>
					<p
						style={{
							margin: 0,
							fontSize: 13,
							color: COLORS.textMuted,
						}}
					>
						{(() => {
							const i = confirmRemove.index
							if (confirmRemove.kind === "adresse") {
								const a = data.adresses[i] || {}
								const label = `${a.rue || ""} ${
									a.code_postal || ""
								} ${a.localite || ""}`
									.replace(/\s+/g, " ")
									.trim()
								return `${
									label ? `« ${label} »` : "Cette adresse"
								} sera définitivement supprimée.`
							}
							const name =
								confirmRemove.kind === "personne"
									? `${(data.personnes[i] || {}).nom || ""} ${
											(data.personnes[i] || {}).prenom ||
											""
										}`.trim()
									: confirmRemove.kind === "assurance"
										? (data.assurances[i] || {}).label || ""
										: (data.fournitures[i] || {}).label ||
											""
							return `${
								name ? `« ${name} »` : "Cet élément"
							} sera définitivement supprimé(e).`
						})()}
					</p>
				</CustomModal>
			)}
		</div>
	)
}

export default EntiteFicheV2
